import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AppShell from "@/components/AppShell";
import KpiCard from "@/components/KpiCard";
import CroHeaderMenu from "@/components/cro/CroHeaderMenu";
import CroSubNav from "@/components/cro/CroSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Loader2, Building2, FlaskConical, ShieldAlert, Activity, TrendingUp, Users, AlertTriangle, IndianRupee, BadgeCheck, Globe, Send, Database, ClipboardCheck, FileCheck2, Handshake, Waypoints, Target, MessageSquareHeart, UserCheck, Dna, Brain, Network, BarChart3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONE = { recruiting: "mint", enrolling: "sky", active: "lavender", completed: "peach" };

export default function CroDashboard() {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab") || "executive";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [portfolio, setPortfolio] = useState(null);

  // Sync when the URL changes (e.g. clicking a top-menu CRO item from another CRO tab)
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t) setActiveTab(t);
  }, [location.search]);
  const [studies, setStudies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [recruitment, setRecruitment] = useState(null);
  const [sites, setSites] = useState(null);
  const [safety, setSafety] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [deviations, setDeviations] = useState(null);
  const [regulatory, setRegulatory] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [digitalTwin, setDigitalTwin] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [patientVoice, setPatientVoice] = useState(null);
  const [investigators, setInvestigators] = useState(null);
  const [rwe, setRwe] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [graph, setGraph] = useState(null);
  const [enterprise, setEnterprise] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, s, r, si, sf, fin, dq, dv, reg, ven, dt, out, pv, inv, rw, pr, gr, ent] = await Promise.all([
        api.get("/cro/portfolio"),
        api.get("/cro/studies"),
        api.get("/cro/recruitment"),
        api.get("/cro/sites"),
        api.get("/cro/safety"),
        api.get("/cro/financial"),
        api.get("/cro/data-quality"),
        api.get("/cro/deviations"),
        api.get("/cro/regulatory"),
        api.get("/cro/vendors"),
        api.get("/cro/digital-twin"),
        api.get("/cro/outcomes"),
        api.get("/cro/patient-voice"),
        api.get("/cro/investigators"),
        api.get("/cro/rwe-biomarkers"),
        api.get("/cro/predictions"),
        api.get("/cro/knowledge-graph"),
        api.get("/cro/enterprise-analytics"),
      ]);
      setPortfolio(p.data);
      setStudies(s.data);
      setRecruitment(r.data);
      setSites(si.data);
      setSafety(sf.data);
      setFinancial(fin.data);
      setDataQuality(dq.data);
      setDeviations(dv.data);
      setRegulatory(reg.data);
      setVendors(ven.data);
      setDigitalTwin(dt.data);
      setOutcomes(out.data);
      setPatientVoice(pv.data);
      setInvestigators(inv.data);
      setRwe(rw.data);
      setPredictions(pr.data);
      setGraph(gr.data);
      setEnterprise(ent.data);
      if (s.data.length) setSelected(s.data[0].id);
    })();
  }, []);

  const study = useMemo(() => studies.find((x) => x.id === selected), [studies, selected]);

  if (!portfolio) return <AppShell pageTitle="Clinical Research Intelligence"><div className="text-sm text-muted-foreground">Loading portfolio…</div></AppShell>;

  const k = portfolio.kpis;
  const ks = portfolio.kpi_series;

  return (
    <AppShell pageTitle="Clinical Research Intelligence" pageDescription="AI-powered research portfolio · Sponsors, CROs, Sites, Investigators, Patients.">
      <CroSubNav />
      <CroHeaderMenu active={activeTab} onChange={setActiveTab} />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
        <div className="hidden" aria-hidden="true">
          <TabsList data-testid="cro-tabs">
            <TabsTrigger value="executive" data-testid="cro-tab-executive">Executive · L1</TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="cro-tab-portfolio">Portfolio · L2</TabsTrigger>
            <TabsTrigger value="study" data-testid="cro-tab-study">Study · L3</TabsTrigger>
            <TabsTrigger value="recruitment" data-testid="cro-tab-recruitment">Recruitment · L4</TabsTrigger>
            <TabsTrigger value="financial" data-testid="cro-tab-financial">Financial · L5</TabsTrigger>
            <TabsTrigger value="dataquality" data-testid="cro-tab-dataquality">Data Quality · L6</TabsTrigger>
            <TabsTrigger value="sites" data-testid="cro-tab-sites">Sites · L8</TabsTrigger>
            <TabsTrigger value="safety" data-testid="cro-tab-safety">Safety · L10</TabsTrigger>
            <TabsTrigger value="deviations" data-testid="cro-tab-deviations">Deviations · L11</TabsTrigger>
            <TabsTrigger value="regulatory" data-testid="cro-tab-regulatory">Regulatory · L7</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="cro-tab-vendors">Vendors · L9</TabsTrigger>
            <TabsTrigger value="digitaltwin" data-testid="cro-tab-digitaltwin">Digital Twin · L12</TabsTrigger>
            <TabsTrigger value="outcomes" data-testid="cro-tab-outcomes">Outcomes · L13</TabsTrigger>
            <TabsTrigger value="patientvoice" data-testid="cro-tab-patientvoice">Patient Voice · L14</TabsTrigger>
            <TabsTrigger value="investigators" data-testid="cro-tab-investigators">Investigators · L15</TabsTrigger>
            <TabsTrigger value="rwe" data-testid="cro-tab-rwe">RWE / Biomarkers · L16</TabsTrigger>
            <TabsTrigger value="predictions" data-testid="cro-tab-predictions">Predictive AI · L17</TabsTrigger>
            <TabsTrigger value="graph" data-testid="cro-tab-graph">Knowledge Graph · L18</TabsTrigger>
            <TabsTrigger value="enterprise" data-testid="cro-tab-enterprise">Enterprise · L19</TabsTrigger>
            <TabsTrigger value="copilot" data-testid="cro-tab-copilot"><Sparkles size={12} className="mr-1" /> Co-Pilot · L20</TabsTrigger>
          </TabsList>
        </div>

        {/* L1 · Executive */}
        <TabsContent value="executive" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Active studies" value={k.active_studies} icon={FlaskConical} tone="sky" testid="cro-kpi-active" />
            <KpiCard label="Sites" value={k.sites} icon={Building2} tone="mint" testid="cro-kpi-sites" />
            <KpiCard label="Countries" value={k.countries} icon={Globe} tone="lavender" testid="cro-kpi-countries" />
            <KpiCard label="Patients enrolled" value={k.patients_enrolled.toLocaleString("en-IN")} icon={Users} tone="peach" testid="cro-kpi-enrolled" />
            <KpiCard label="Patients screened" value={k.patients_screened.toLocaleString("en-IN")} icon={Activity} tone="lemon" />
            <KpiCard label="Adverse events" value={k.adverse_events} unit={`(${k.serious_adverse_events} serious)`} icon={AlertTriangle} tone="rose" />
            <KpiCard label="Trial completion" value={k.trial_completion_pct} unit="%" icon={TrendingUp} tone="mint" />
            <KpiCard label="AI risk index" value={k.ai_risk_index} unit="/100" icon={ShieldAlert} tone="peach" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border bg-white p-6" data-testid="cro-kpi-series">
              <div className="overline">Operational KPIs</div>
              <ul className="mt-3 divide-y divide-border text-sm">
                {Object.entries(ks).map(([k2, v]) => (
                  <li key={k2} className="py-2 flex items-center justify-between">
                    <span className="text-muted-foreground capitalize">{k2.replace(/_/g, " ")}</span>
                    <span className="font-medium text-primary">{String(v)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-border bg-white p-6" data-testid="cro-by-ta">
              <div className="overline">Portfolio by therapeutic area</div>
              <ul className="mt-3 space-y-3">
                {portfolio.by_therapeutic_area.map((t) => (
                  <li key={t.area}>
                    <div className="flex items-center justify-between text-sm">
                      <div className="font-medium text-primary">{t.area}</div>
                      <div className="text-xs text-muted-foreground">{t.studies} studies · {t.sites} sites · {t.enrolled.toLocaleString("en-IN")} enrolled</div>
                    </div>
                    <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${Math.min(100, (t.enrolled / Math.max(1, k.patients_enrolled)) * 100)}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground">
            <strong>All 20 layers live 🎉</strong> — Complete CRO/CTO Stack: L1 Executive, L2 Portfolio, L3 Study, L4 Recruitment, L5 Financial, L6 Data Quality, L7 Regulatory, L8 Sites, L9 Vendors, L10 Safety, L11 Deviations, L12 Digital Twin, L13 Outcomes, L14 Patient Voice, L15 Investigators, L16 RWE/Biomarkers, L17 Predictive AI, L18 Knowledge Graph, L19 Enterprise Analytics, L20 Co-Pilot.
          </div>
        </TabsContent>

        {/* L2 · Portfolio */}
        <TabsContent value="portfolio" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cro-portfolio-grid">
            {studies.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s.id)}
                className={`text-left rounded-lg border p-5 transition-all hover:-translate-y-0.5 hover:shadow-sm ${s.id === selected ? "border-primary bg-white" : "border-border bg-white"}`}
                data-testid={`cro-study-card-${s.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 bg-pastel-${STATUS_TONE[s.status] || "mint"}-bg text-pastel-${STATUS_TONE[s.status] || "mint"}-fg`}>
                    {s.status}
                  </span>
                  <span className="text-xs text-muted-foreground">{s.phase}</span>
                </div>
                <div className="mt-2 font-medium text-primary">{s.name}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.therapeutic_area} · {s.sponsor} → {s.cro}</div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                  <Mini label="Sites" value={s.site_count} />
                  <Mini label="Enrolled" value={s.patients_enrolled.toLocaleString("en-IN")} />
                  <Mini label="AE" value={s.adverse_events} />
                  <Mini label="AI risk" value={s.ai_risk_score} />
                </div>
              </button>
            ))}
          </div>
        </TabsContent>

        {/* L3 · Study Workspace */}
        <TabsContent value="study" className="mt-6">
          {study ? <StudyWorkspace study={study} /> : <div className="text-sm text-muted-foreground">Pick a study from Portfolio.</div>}
        </TabsContent>

        {/* L4 · Recruitment */}
        <TabsContent value="recruitment" className="mt-6">
          {recruitment && <Recruitment data={recruitment} />}
        </TabsContent>

        {/* L5 · Financial */}
        <TabsContent value="financial" className="mt-6">
          {financial && <Financial data={financial} />}
        </TabsContent>

        {/* L6 · Data Quality */}
        <TabsContent value="dataquality" className="mt-6">
          {dataQuality && <DataQuality data={dataQuality} />}
        </TabsContent>

        {/* L8 · Sites */}
        <TabsContent value="sites" className="mt-6">
          {sites && <SiteLeaderboard data={sites} />}
        </TabsContent>

        {/* L10 · Safety */}
        <TabsContent value="safety" className="mt-6">
          {safety && <SafetyDeepdive data={safety} />}
        </TabsContent>

        {/* L11 · Protocol Deviations */}
        <TabsContent value="deviations" className="mt-6">
          {deviations && <Deviations data={deviations} />}
        </TabsContent>

        {/* L7 · Regulatory */}
        <TabsContent value="regulatory" className="mt-6">
          {regulatory && <Regulatory data={regulatory} />}
        </TabsContent>

        {/* L9 · Vendors */}
        <TabsContent value="vendors" className="mt-6">
          {vendors && <Vendors data={vendors} />}
        </TabsContent>

        {/* L12 · Digital Twin */}
        <TabsContent value="digitaltwin" className="mt-6">
          {digitalTwin && <DigitalTwin data={digitalTwin} />}
        </TabsContent>

        {/* L13 · Outcomes */}
        <TabsContent value="outcomes" className="mt-6">
          {outcomes && <Outcomes data={outcomes} />}
        </TabsContent>

        {/* L14 · Patient Voice */}
        <TabsContent value="patientvoice" className="mt-6">
          {patientVoice && <PatientVoice data={patientVoice} />}
        </TabsContent>

        {/* L15 · Investigators */}
        <TabsContent value="investigators" className="mt-6">
          {investigators && <Investigators data={investigators} />}
        </TabsContent>

        {/* L16 · RWE / Biomarkers */}
        <TabsContent value="rwe" className="mt-6">
          {rwe && <RweBiomarkers data={rwe} />}
        </TabsContent>

        {/* L17 · Predictive AI */}
        <TabsContent value="predictions" className="mt-6">
          {predictions && <Predictions data={predictions} />}
        </TabsContent>

        {/* L18 · Knowledge Graph */}
        <TabsContent value="graph" className="mt-6">
          {graph && <KnowledgeGraph data={graph} />}
        </TabsContent>

        {/* L19 · Enterprise Analytics */}
        <TabsContent value="enterprise" className="mt-6">
          {enterprise && <Enterprise data={enterprise} />}
        </TabsContent>

        {/* L20 · AI Co-Pilot */}
        <TabsContent value="copilot" className="mt-6">
          <CoPilot studyId={selected} />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-md bg-muted/50 p-2">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono text-primary mt-0.5">{value}</div>
    </div>
  );
}

function StudyWorkspace({ study }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid={`cro-study-workspace-${study.id}`}>
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="overline">{study.therapeutic_area} · {study.phase}</div>
          <h2 className="mt-2 font-serif text-3xl text-primary">{study.name}</h2>
          <div className="mt-1 text-sm text-muted-foreground">{study.sponsor} → {study.cro}</div>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Fact k="Status" v={study.status} />
            <Fact k="Regulatory" v={study.regulatory_status} />
            <Fact k="Ethics" v={`${study.ethics_approvals} approvals`} />
            <Fact k="Started" v={study.start_date} />
            <Fact k="Sites" v={study.site_count} />
            <Fact k="Target" v={study.patients_target.toLocaleString("en-IN")} />
            <Fact k="Enrolled" v={study.patients_enrolled.toLocaleString("en-IN")} />
            <Fact k="Screened" v={study.patients_screened.toLocaleString("en-IN")} />
          </div>
        </div>
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="overline mb-3">Study timeline</div>
          <ol className="relative border-l-2 border-border ml-2 space-y-4">
            {study.milestones.map((m, i) => (
              <li key={i} className="pl-5">
                <span className={`absolute -left-[9px] w-4 h-4 rounded-full border-2 border-background ${m.status === "done" ? "bg-primary" : "bg-muted-foreground"}`} />
                <div className="text-xs text-muted-foreground">{m.date}</div>
                <div className="text-sm font-medium text-primary">{m.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.status}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <div className="space-y-4">
        <div className="rounded-lg border border-pastel-sky-border bg-pastel-sky-bg p-5">
          <div className="overline text-pastel-sky-fg/80">AI risk score</div>
          <div className="mt-1 font-serif text-5xl text-pastel-sky-fg">{study.ai_risk_score}</div>
          <div className="text-xs text-pastel-sky-fg/70">/100 · lower is better</div>
        </div>
        <div className="rounded-lg border border-border bg-white p-5">
          <div className="overline mb-2">Safety snapshot</div>
          <dl className="text-sm space-y-2">
            <Row k="Adverse events" v={study.adverse_events} />
            <Row k="Serious AE" v={study.serious_adverse_events} tone="destructive" />
            <Row k="Protocol deviations" v={study.protocol_deviations} />
            <Row k="Dropouts" v={study.dropouts} />
            <Row k="Completion" v={`${study.completion_pct}%`} />
            <Row k="Budget used" v={`${study.budget_used_pct}%`} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function Fact({ k, v }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{k}</div>
      <div className="text-primary font-medium capitalize">{v}</div>
    </div>
  );
}
function Row({ k, v, tone }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={`font-medium ${tone === "destructive" ? "text-destructive" : "text-primary"}`}>{v}</dd>
    </div>
  );
}

const SUGGESTED = [
  "Which studies are highest risk right now?",
  "Compare enrolment progress across cardiology and oncology.",
  "Which sponsor has the most active studies?",
  "Summarise serious adverse events across the portfolio.",
];

// ---------------- L4 · Recruitment ----------------
function Recruitment({ data }) {
  const t = data.total;
  const funnel = [
    { key: "screened",    label: "Screened",     value: t.screened, tone: "sky" },
    { key: "screened_in", label: "Screened-in",  value: t.screened_in, tone: "mint" },
    { key: "randomised",  label: "Randomised",   value: t.randomised, tone: "lavender" },
    { key: "completed",   label: "Completed",    value: t.completed, tone: "peach" },
  ];
  const max = funnel[0].value || 1;
  return (
    <div className="space-y-6" data-testid="cro-recruitment">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {funnel.map((f) => (
          <KpiCard key={f.key} label={f.label} value={f.value.toLocaleString("en-IN")} icon={Users} tone={f.tone} testid={`funnel-${f.key}`} />
        ))}
      </div>
      <div className="rounded-lg border border-border bg-white p-6">
        <div className="overline mb-3">Portfolio funnel</div>
        <div className="space-y-3">
          {funnel.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-primary">{f.label}</span>
                <span className="text-muted-foreground">{f.value.toLocaleString("en-IN")} · {Math.round((f.value / max) * 100)}%</span>
              </div>
              <div className="mt-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full bg-pastel-${f.tone}-fg`} style={{ width: `${(f.value / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="overline mb-3">Per study — % of target enrolled</div>
          <ul className="space-y-3 text-sm" data-testid="funnel-per-study">
            {data.studies.map((s) => (
              <li key={s.id}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium truncate max-w-[70%]">{s.name}</span>
                  <span className="text-xs text-muted-foreground">{s.randomised}/{s.target} · drop {s.drop_rate_pct}%</span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${Math.min(100, s.target_pct)}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-pastel-peach-border bg-pastel-peach-bg p-6" data-testid="funnel-drivers">
          <div className="overline text-pastel-peach-fg/80 mb-3">Drop-off drivers</div>
          <ul className="space-y-2 text-sm text-pastel-peach-fg">
            {data.drop_off_drivers.map((d) => (
              <li key={d.reason} className="flex items-center justify-between">
                <span>{d.reason}</span>
                <span className="font-mono">{d.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------------- L8 · Sites ----------------
function SiteLeaderboard({ data }) {
  return (
    <div className="space-y-6" data-testid="cro-sites">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Total sites" value={data.total_sites} icon={Building2} tone="sky" />
        <KpiCard label="Top rate / month" value={data.top[0]?.enrollment_rate_per_month || 0} icon={TrendingUp} tone="mint" />
        <KpiCard label="Underperforming sites" value={data.bottom.length} icon={AlertTriangle} tone="peach" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SiteList title="Top performers" testid="sites-top" items={data.top} tone="mint" />
        <SiteList title="Bottom performers · action needed" testid="sites-bottom" items={data.bottom} tone="rose" />
      </div>
    </div>
  );
}
function SiteList({ title, items, tone, testid }) {
  return (
    <div className={`rounded-lg border border-pastel-${tone}-border bg-pastel-${tone}-bg p-5`} data-testid={testid}>
      <div className={`overline text-pastel-${tone}-fg/80 mb-3`}>{title}</div>
      <ul className={`space-y-2.5 text-sm text-pastel-${tone}-fg`}>
        {items.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium truncate">#{s.rank} · {s.site_name}</div>
              <div className="text-[11px] opacity-80 truncate">{s.study_name}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono">{s.enrollment_rate_per_month}/mo</div>
              <div className="text-[10px] opacity-70">DQ {s.data_quality_score} · dev {s.deviations}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------- L10 · Safety ----------------
function SafetyDeepdive({ data }) {
  const maxSoc = Math.max(...data.meddra_soc.map((m) => m.ae), 1);
  const maxTrend = Math.max(...data.monthly_trend.map((t) => t.ae), 1);
  return (
    <div className="space-y-6" data-testid="cro-safety">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Adverse events" value={data.totals.ae.toLocaleString("en-IN")} icon={AlertTriangle} tone="peach" testid="safety-total-ae" />
        <KpiCard label="Serious AE" value={data.totals.sae} icon={ShieldAlert} tone="rose" testid="safety-total-sae" />
        <KpiCard label="SAE rate / 100 enrolled" value={data.totals.sae_rate_per_100_enrolled} icon={Activity} tone="lemon" testid="safety-sae-rate" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="safety-meddra">
          <div className="overline mb-3">MedDRA · SOC distribution</div>
          <ul className="space-y-3 text-sm">
            {data.meddra_soc.map((m) => (
              <li key={m.soc}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium truncate max-w-[60%]">{m.soc}</span>
                  <span className="text-xs text-muted-foreground">AE {m.ae} · SAE {m.sae}</span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${(m.ae / maxSoc) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-white p-6" data-testid="safety-trend">
          <div className="overline mb-3">12-month trend</div>
          <div className="grid grid-cols-12 gap-1 h-32 items-end">
            {data.monthly_trend.map((t) => (
              <div key={t.month} className="flex flex-col items-center gap-1">
                <div className="w-full flex flex-col justify-end h-24">
                  <div className="bg-primary rounded-sm" style={{ height: `${(t.ae / maxTrend) * 100}%` }} title={`AE ${t.ae} · SAE ${t.sae}`} />
                </div>
                <div className="text-[9px] text-muted-foreground">{t.month}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="safety-per-study">
        <div className="overline mb-3">SAE rate by study</div>
        <ul className="space-y-2.5 text-sm">
          {data.per_study.map((s) => (
            <li key={s.id} className="flex items-center justify-between">
              <span className="text-primary font-medium truncate max-w-[60%]">{s.name}</span>
              <span className="text-xs text-muted-foreground">SAE {s.sae} · rate {s.sae_rate_per_100}/100 · AI risk {s.ai_risk}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg border border-pastel-rose-border bg-pastel-rose-bg p-6" data-testid="safety-signals">
        <div className="overline text-pastel-rose-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI safety signals</div>
        <ul className="space-y-2 text-sm text-pastel-rose-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L5 · Financial ----------------
function Financial({ data }) {
  const t = data.totals;
  const maxSpent = Math.max(...data.per_study.map((s) => s.spent_inr_cr), 1);
  const maxCat = Math.max(...data.categories.map((c) => c.spent_inr_cr), 1);
  return (
    <div className="space-y-6" data-testid="cro-financial">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total budget" value={`₹${t.budget_inr_cr} Cr`} icon={IndianRupee} tone="sky" testid="fin-budget" />
        <KpiCard label="Spent" value={`₹${t.spent_inr_cr} Cr`} unit={`(${t.used_pct}%)`} icon={TrendingUp} tone="peach" testid="fin-spent" />
        <KpiCard label="Remaining" value={`₹${t.remaining_inr_cr} Cr`} icon={IndianRupee} tone="mint" testid="fin-remaining" />
        <KpiCard label="Cost / patient" value={`₹${t.cost_per_patient_lakh} L`} icon={Users} tone="lavender" testid="fin-cost-per-patient" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="fin-per-study">
          <div className="overline mb-3">Per-study burn vs completion</div>
          <ul className="space-y-3 text-sm">
            {data.per_study.map((s) => (
              <li key={s.id}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium truncate max-w-[55%]">{s.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.flag === "over" ? "bg-destructive/15 text-destructive" : s.flag === "under" ? "bg-secondary/20 text-secondary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {s.flag} · burn {s.burn_ratio}
                  </span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${(s.spent_inr_cr / maxSpent) * 100}%` }} />
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  ₹{s.spent_inr_cr} Cr of ₹{s.budget_inr_cr} Cr · used {s.used_pct}% · completion {s.completion_pct}% · forecast final {s.forecast_final_pct ?? "—"}%
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-white p-6" data-testid="fin-categories">
          <div className="overline mb-3">Spend by category</div>
          <ul className="space-y-3 text-sm">
            {data.categories.map((c) => (
              <li key={c.name}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">₹{c.spent_inr_cr} Cr · {c.share_pct}%</span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${(c.spent_inr_cr / maxCat) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="rounded-lg border border-pastel-peach-border bg-pastel-peach-bg p-6" data-testid="fin-signals">
        <div className="overline text-pastel-peach-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI financial signals</div>
        <ul className="space-y-2 text-sm text-pastel-peach-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L6 · Data Quality ----------------
function DataQuality({ data }) {
  const t = data.totals;
  const maxQ = Math.max(...data.query_types.map((q) => q.count), 1);
  return (
    <div className="space-y-6" data-testid="cro-dataquality">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Portfolio DQ score" value={t.portfolio_data_quality} unit="/100" icon={BadgeCheck} tone="mint" testid="dq-score" />
        <KpiCard label="SDV coverage" value={`${t.portfolio_sdv_pct}%`} icon={ClipboardCheck} tone="sky" testid="dq-sdv" />
        <KpiCard label="Open queries" value={t.open_queries.toLocaleString("en-IN")} icon={Database} tone="peach" testid="dq-open" />
        <KpiCard label="Form completeness" value={`${t.form_completeness_pct}%`} icon={Activity} tone="lavender" testid="dq-forms" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="dq-per-study">
          <div className="overline mb-3">Per-study data quality</div>
          <ul className="space-y-3 text-sm">
            {data.per_study.map((s) => (
              <li key={s.id}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium truncate max-w-[55%]">{s.name}</span>
                  <span className="text-xs text-muted-foreground">DQ {s.data_quality_score} · SDV {s.sdv_pct}% · lock {s.db_lock_readiness_pct}%</span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${s.data_quality_score >= 85 ? "bg-secondary" : s.data_quality_score >= 70 ? "bg-accent" : "bg-destructive"}`} style={{ width: `${s.data_quality_score}%` }} />
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Forms {s.forms_captured.toLocaleString("en-IN")} / {s.forms_expected.toLocaleString("en-IN")} · open Q {s.open_queries} (·&gt;30d {s.query_aging.over_30d}) · resolved {s.resolved_queries}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-white p-6" data-testid="dq-query-types">
          <div className="overline mb-3">Open query breakdown</div>
          <ul className="space-y-3 text-sm">
            {data.query_types.map((q) => (
              <li key={q.type}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium">{q.type}</span>
                  <span className="text-xs text-muted-foreground">{q.count}</span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${(q.count / maxQ) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="rounded-lg border border-pastel-mint-border bg-pastel-mint-bg p-6" data-testid="dq-signals">
        <div className="overline text-pastel-mint-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI data-quality signals</div>
        <ul className="space-y-2 text-sm text-pastel-mint-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L11 · Protocol Deviations ----------------
function Deviations({ data }) {
  const t = data.totals;
  const maxCat = Math.max(...data.categories.map((c) => c.count), 1);
  const maxTrend = Math.max(...data.monthly_trend.map((m) => m.major + m.minor), 1);
  return (
    <div className="space-y-6" data-testid="cro-deviations">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total deviations" value={t.total} icon={AlertTriangle} tone="peach" testid="dev-total" />
        <KpiCard label="Major" value={t.major} unit={`(${t.major_pct}%)`} icon={ShieldAlert} tone="rose" testid="dev-major" />
        <KpiCard label="Minor" value={t.minor} icon={ClipboardCheck} tone="mint" testid="dev-minor" />
        <KpiCard label="Rate / 100 enrolled" value={t.rate_per_100_enrolled} icon={Activity} tone="lemon" testid="dev-rate" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="dev-categories">
          <div className="overline mb-3">Top deviation categories</div>
          <ul className="space-y-3 text-sm">
            {data.categories.map((c) => (
              <li key={c.category}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium truncate max-w-[65%]">{c.category}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.severity === "major" ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground"}`}>
                    {c.severity} · {c.count}
                  </span>
                </div>
                <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${c.severity === "major" ? "bg-destructive" : "bg-primary"}`} style={{ width: `${(c.count / maxCat) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-white p-6" data-testid="dev-trend">
          <div className="overline mb-3">12-month deviation trend</div>
          <div className="grid grid-cols-12 gap-1 h-32 items-end">
            {data.monthly_trend.map((m) => (
              <div key={m.month} className="flex flex-col items-center gap-1">
                <div className="w-full flex flex-col justify-end h-24">
                  <div className="bg-destructive/80 rounded-t-sm" style={{ height: `${(m.major / maxTrend) * 100}%` }} title={`Major ${m.major}`} />
                  <div className="bg-primary/70 rounded-b-sm" style={{ height: `${(m.minor / maxTrend) * 100}%` }} title={`Minor ${m.minor}`} />
                </div>
                <div className="text-[9px] text-muted-foreground">{m.month}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-destructive/80 inline-block rounded-sm" /> Major</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-primary/70 inline-block rounded-sm" /> Minor</span>
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="dev-per-study">
        <div className="overline mb-3">Per-study deviation rate</div>
        <ul className="space-y-2.5 text-sm">
          {data.per_study.map((s) => (
            <li key={s.id} className="flex items-center justify-between">
              <span className="text-primary font-medium truncate max-w-[60%]">{s.name}</span>
              <span className="text-xs text-muted-foreground">Total {s.total} · major {s.major} · rate {s.rate_per_100_enrolled}/100 · AI risk {s.ai_risk}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg border border-pastel-rose-border bg-pastel-rose-bg p-6" data-testid="dev-signals">
        <div className="overline text-pastel-rose-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI compliance signals</div>
        <ul className="space-y-2 text-sm text-pastel-rose-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L7 · Regulatory ----------------
const STATUS_TONE_REG = {
  approved: "mint",
  pending: "sky",
  under_review: "lavender",
  queries_raised: "peach",
  in_preparation: "lemon",
};
function Regulatory({ data }) {
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="cro-regulatory">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Total submissions" value={t.total} icon={FileCheck2} tone="sky" testid="reg-total" />
        <KpiCard label="Approved" value={t.approved} unit={`(${t.approval_pct}%)`} icon={BadgeCheck} tone="mint" testid="reg-approved" />
        <KpiCard label="Overdue" value={t.overdue} icon={AlertTriangle} tone="rose" testid="reg-overdue" />
        <KpiCard label="Due in 60 days" value={t.upcoming_60d} icon={Activity} tone="peach" testid="reg-upcoming" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="reg-by-authority">
          <div className="overline mb-3">By regulatory authority</div>
          <ul className="space-y-3 text-sm">
            {data.by_authority.map((a) => (
              <li key={a.authority}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium">{a.authority}</span>
                  <span className="text-xs text-muted-foreground">{a.total} filings</span>
                </div>
                <div className="mt-1 flex gap-1 h-1.5 rounded-full overflow-hidden bg-muted">
                  {["approved", "under_review", "pending", "queries_raised", "in_preparation"].map((k) =>
                    a[k] ? <div key={k} className={`h-full bg-pastel-${STATUS_TONE_REG[k]}-fg`} style={{ width: `${(a[k] / a.total) * 100}%` }} title={`${k}: ${a[k]}`} /> : null
                  )}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Approved {a.approved || 0} · Under review {a.under_review || 0} · Pending {a.pending || 0} · Queries {a.queries_raised || 0} · Prep {a.in_preparation || 0}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-pastel-peach-border bg-pastel-peach-bg p-6" data-testid="reg-overdue-list">
          <div className="overline text-pastel-peach-fg/80 mb-3">Overdue submissions · action required</div>
          {data.overdue.length === 0 ? (
            <div className="text-sm text-pastel-peach-fg">✓ No overdue submissions.</div>
          ) : (
            <ul className="space-y-2 text-sm text-pastel-peach-fg">
              {data.overdue.map((s) => (
                <li key={s.id} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{s.type}</div>
                    <div className="text-[11px] opacity-80 truncate">{s.authority} · {s.study_name} · {s.reference}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-destructive">{Math.abs(s.days_to_due)}d late</div>
                    <div className="text-[10px] opacity-70">{s.due_date}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="reg-upcoming-list">
        <div className="overline mb-3">Upcoming in the next 60 days</div>
        {data.upcoming.length === 0 ? (
          <div className="text-sm text-muted-foreground">No submissions due in the next 60 days.</div>
        ) : (
          <ul className="space-y-2 text-sm">
            {data.upcoming.map((s) => (
              <li key={s.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-primary truncate">{s.type}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{s.authority} · {s.study_name} · {s.reference}</div>
                </div>
                <div className="shrink-0 text-right">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-${STATUS_TONE_REG[s.status] || "sky"}-bg text-pastel-${STATUS_TONE_REG[s.status] || "sky"}-fg`}>{s.status.replace(/_/g, " ")}</span>
                  <div className="mt-1 text-[10px] text-muted-foreground">Due in {s.days_to_due}d · {s.due_date}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="rounded-lg border border-pastel-lavender-border bg-pastel-lavender-bg p-6" data-testid="reg-signals">
        <div className="overline text-pastel-lavender-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI regulatory signals</div>
        <ul className="space-y-2 text-sm text-pastel-lavender-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L9 · Vendors / CRO Oversight ----------------
const RATING_TONE = { A: "mint", B: "sky", C: "peach", D: "rose" };
function Vendors({ data }) {
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="cro-vendors">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="CRO partners" value={t.vendors} icon={Handshake} tone="sky" testid="ven-count" />
        <KpiCard label="Studies managed" value={t.studies} icon={FlaskConical} tone="mint" testid="ven-studies" />
        <KpiCard label="Contract value" value={`₹${t.contract_value_cr} Cr`} icon={IndianRupee} tone="lavender" testid="ven-contract" />
        <KpiCard label="Avg scorecard" value={t.avg_scorecard} unit="/100" icon={BadgeCheck} tone="peach" testid="ven-avg" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ven-cards">
        {data.vendors.map((v) => (
          <div key={v.name} className={`rounded-lg border border-pastel-${RATING_TONE[v.rating]}-border bg-white p-6`} data-testid={`ven-card-${v.name.replace(/\s+/g,"-").toLowerCase()}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-serif text-xl text-primary truncate">{v.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{v.study_count} study(ies) · {v.sites} sites · {v.enrolled.toLocaleString("en-IN")} enrolled</div>
              </div>
              <span className={`shrink-0 rounded-full w-10 h-10 inline-flex items-center justify-center font-serif text-xl bg-pastel-${RATING_TONE[v.rating]}-bg text-pastel-${RATING_TONE[v.rating]}-fg border border-pastel-${RATING_TONE[v.rating]}-border`} data-testid="ven-rating">
                {v.rating}
              </span>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-xs">
              <ScoreBar label="Timeliness" value={v.scorecard.timeliness} />
              <ScoreBar label="Quality" value={v.scorecard.quality} />
              <ScoreBar label="Budget" value={v.scorecard.budget_adherence} />
              <ScoreBar label="Safety" value={v.scorecard.safety} />
            </div>
            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Overall</span>
              <span className="font-mono text-primary text-base">{v.scorecard.overall}/100</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
              <div>Budget<br /><span className="text-primary font-medium">₹{v.budget_cr} Cr</span></div>
              <div>Spent<br /><span className="text-primary font-medium">₹{v.spent_cr.toFixed(1)} Cr</span></div>
              <div>Deviations<br /><span className="text-primary font-medium">{v.deviations}</span></div>
            </div>
            {v.issues.length > 0 && (
              <ul className="mt-3 space-y-1 text-[11px] text-destructive">
                {v.issues.map((issue) => (
                  <li key={issue} className="flex items-start gap-1.5"><AlertTriangle size={11} className="mt-0.5 shrink-0" /><span>{issue}</span></li>
                ))}
              </ul>
            )}
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-primary">Study list ({v.study_count})</summary>
              <ul className="mt-2 space-y-1 pl-2">
                {v.studies.map((st) => (
                  <li key={st.id} className="text-primary truncate">• {st.name} <span className="text-muted-foreground">· {st.phase} · {st.status}</span></li>
                ))}
              </ul>
            </details>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-pastel-sky-border bg-pastel-sky-bg p-6" data-testid="ven-signals">
        <div className="overline text-pastel-sky-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI vendor oversight signals</div>
        <ul className="space-y-2 text-sm text-pastel-sky-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
function ScoreBar({ label, value }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{label}</span>
        <span className="font-mono text-primary">{value}</span>
      </div>
      <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${value >= 85 ? "bg-secondary" : value >= 70 ? "bg-accent" : "bg-destructive"}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

// ---------------- L12 · Digital Twin ----------------
const SCENARIO_TONE = { baseline: "sky", add_sites: "mint", extend_window: "peach", relax_ie: "lemon", digital_outreach: "lavender" };
function DigitalTwin({ data }) {
  const p = data.portfolio;
  const [openStudy, setOpenStudy] = useState(data.per_study[0]?.id);
  const active = data.per_study.find((s) => s.id === openStudy) || data.per_study[0];
  return (
    <div className="space-y-6" data-testid="cro-digitaltwin">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Baseline enrolment" value={`${p.avg_baseline_enrolment_pct}%`} icon={Activity} tone="sky" testid="dt-baseline" />
        <KpiCard label="Recommended lift" value={`${p.avg_recommended_enrolment_pct}%`} icon={TrendingUp} tone="mint" testid="dt-recommended" />
        <KpiCard label="Cost of recommendations" value={`+₹${p.total_cost_delta_cr} Cr`} icon={IndianRupee} tone="peach" testid="dt-cost" />
        <KpiCard label="Avg timeline shift" value={`${p.avg_timeline_delta_days > 0 ? "+" : ""}${p.avg_timeline_delta_days}d`} icon={Waypoints} tone="lavender" testid="dt-timeline" />
      </div>
      <div className="rounded-lg border border-border bg-white p-4" data-testid="dt-study-picker">
        <div className="flex gap-2 flex-wrap">
          {data.per_study.map((s) => (
            <button key={s.id}
              onClick={() => setOpenStudy(s.id)}
              data-testid={`dt-study-${s.id}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${openStudy === s.id ? "bg-primary text-primary-foreground border-primary" : "bg-white text-primary border-border hover:bg-muted"}`}>
              {s.name} · {s.phase}
            </button>
          ))}
        </div>
      </div>
      {active && (
        <div className="rounded-lg border border-border bg-white p-6" data-testid="dt-scenarios">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Digital twin · what-if scenarios</div>
              <div className="text-sm text-muted-foreground mt-1">
                {active.name} · target {active.target.toLocaleString("en-IN")} · currently {active.enrolled.toLocaleString("en-IN")} enrolled · gap {active.gap_pct}%
              </div>
            </div>
            <span className="text-xs px-2.5 py-1 rounded-full bg-pastel-mint-bg text-pastel-mint-fg border border-pastel-mint-border">
              ★ Recommended: {active.scenarios.find((sc) => sc.id === active.recommended_scenario_id).name}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mb-4">{active.recommended_reason}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="dt-scenario-table">
              <thead className="text-muted-foreground text-left">
                <tr>
                  <th className="py-2">Scenario</th>
                  <th className="py-2 text-right">Final enrol</th>
                  <th className="py-2 text-right">Timeline</th>
                  <th className="py-2 text-right">Cost Δ</th>
                  <th className="py-2 text-right">Dropout risk</th>
                  <th className="py-2 text-right">Power</th>
                </tr>
              </thead>
              <tbody>
                {active.scenarios.map((sc) => (
                  <tr key={sc.id} className={`border-t border-border ${sc.id === active.recommended_scenario_id ? "bg-pastel-mint-bg/40" : ""}`}>
                    <td className="py-2 pr-2">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 bg-pastel-${SCENARIO_TONE[sc.id]}-fg`} />
                      {sc.name}
                    </td>
                    <td className="py-2 text-right font-mono">{sc.final_enrolment_pct}%</td>
                    <td className="py-2 text-right font-mono">{sc.timeline_delta_days > 0 ? "+" : ""}{sc.timeline_delta_days}d</td>
                    <td className="py-2 text-right font-mono">₹{sc.cost_delta_cr} Cr</td>
                    <td className="py-2 text-right font-mono">{sc.dropout_risk_pct}%</td>
                    <td className="py-2 text-right font-mono">{sc.power}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="rounded-lg border border-pastel-lavender-border bg-pastel-lavender-bg p-6" data-testid="dt-signals">
        <div className="overline text-pastel-lavender-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI digital-twin signals</div>
        <ul className="space-y-2 text-sm text-pastel-lavender-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L13 · Outcomes ----------------
const SIG_TONE = { significant: "mint", trend: "peach", not_significant: "rose" };
function Outcomes({ data }) {
  const p = data.portfolio;
  const maxEffect = Math.max(...data.by_therapeutic_area.map((t) => Math.abs(t.avg_effect)), 1);
  return (
    <div className="space-y-6" data-testid="cro-outcomes">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Studies analysed" value={p.studies_analysed} icon={FlaskConical} tone="sky" testid="out-count" />
        <KpiCard label="Statistically significant" value={p.significant_studies} unit={`/ ${p.studies_analysed}`} icon={BadgeCheck} tone="mint" testid="out-sig" />
        <KpiCard label="Avg absolute effect" value={`${p.avg_effect_pct}pp`} icon={Target} tone="lavender" testid="out-effect" />
        <KpiCard label="Avg response" value={`${p.avg_response_pct}%`} icon={Activity} tone="peach" testid="out-response" />
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="out-per-study">
        <div className="overline mb-3">Per-study outcomes (sorted by effect size)</div>
        <div className="space-y-4">
          {data.per_study.map((s) => (
            <div key={s.id} className="border-t border-border pt-3 first:border-0 first:pt-0" data-testid={`out-study-${s.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-primary font-medium truncate">{s.name}</div>
                  <div className="text-[11px] text-muted-foreground">{s.therapeutic_area} · {s.primary_endpoint}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-${SIG_TONE[s.significance]}-bg text-pastel-${SIG_TONE[s.significance]}-fg border border-pastel-${SIG_TONE[s.significance]}-border`}>
                    {s.significance.replace(/_/g, " ")}
                  </span>
                  <span className="font-mono text-primary text-sm">p = {s.p_value}</span>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-muted-foreground">Treatment:</span> <span className="font-mono">{s.arm_response_pct}%</span></div>
                <div><span className="text-muted-foreground">Control:</span> <span className="font-mono">{s.control_response_pct}%</span></div>
                <div><span className="text-muted-foreground">Effect:</span> <span className="font-mono">{s.absolute_effect_pct}pp <span className="opacity-70">[{s.ci_lower}, {s.ci_upper}]</span></span></div>
                <div><span className="text-muted-foreground">{s.metric_name.replace(/_/g, " ")}:</span> <span className="font-mono">{s.metric_value}</span></div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                {["gender", "age_band", "region"].map((k) => (
                  <div key={k} className="rounded-md bg-muted/40 p-2">
                    <div className="overline text-muted-foreground mb-1">{k.replace(/_/g, " ")}</div>
                    <ul className="space-y-0.5">
                      {s.subgroups[k].map((sg) => (
                        <li key={sg.label} className="flex justify-between">
                          <span className="text-primary">{sg.label}</span>
                          <span className="font-mono text-muted-foreground">n={sg.n} · {sg.response_rate}%</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="out-by-ta">
        <div className="overline mb-3">By therapeutic area</div>
        <ul className="space-y-3 text-sm">
          {data.by_therapeutic_area.map((t) => (
            <li key={t.area}>
              <div className="flex items-center justify-between">
                <span className="text-primary font-medium truncate max-w-[65%]">{t.area}</span>
                <span className="text-xs text-muted-foreground">{t.studies} study · {t.sig} significant · Δ {t.avg_effect}pp</span>
              </div>
              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${(Math.abs(t.avg_effect) / maxEffect) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg border border-pastel-mint-border bg-pastel-mint-bg p-6" data-testid="out-signals">
        <div className="overline text-pastel-mint-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI outcomes signals</div>
        <ul className="space-y-2 text-sm text-pastel-mint-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L14 · Patient Voice ----------------
function PatientVoice({ data }) {
  const p = data.portfolio;
  const maxDrop = Math.max(...data.dropout_reasons.map((d) => d.count), 1);
  return (
    <div className="space-y-6" data-testid="cro-patientvoice">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Overall satisfaction" value={`${p.avg_overall_satisfaction}/5`} icon={MessageSquareHeart} tone="mint" testid="pv-satisfaction" />
        <KpiCard label="Would recommend" value={`${p.avg_would_recommend_pct}%`} icon={BadgeCheck} tone="sky" testid="pv-recommend" />
        <KpiCard label="PRO responses" value={p.responses.toLocaleString("en-IN")} unit={`(${p.response_rate_pct}%)`} icon={Users} tone="lavender" testid="pv-responses" />
        <KpiCard label="Dropout rate" value={`${p.portfolio_dropout_rate_pct}%`} unit={`(${p.total_dropouts})`} icon={AlertTriangle} tone="peach" testid="pv-dropouts" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="pv-per-study">
          <div className="overline mb-3">Per-study PRO scores</div>
          <ul className="space-y-4 text-sm">
            {data.per_study.map((s) => (
              <li key={s.id} className="border-b border-border pb-3 last:border-0" data-testid={`pv-study-${s.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-primary font-medium truncate max-w-[65%]">{s.name}</span>
                  <span className="font-mono text-primary">{s.scores.overall_satisfaction}/5</span>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                  <div>Communication: <span className="font-mono text-primary">{s.scores.communication}</span></div>
                  <div>Site experience: <span className="font-mono text-primary">{s.scores.site_experience}</span></div>
                  <div>Ease of participation: <span className="font-mono text-primary">{s.scores.ease_of_participation}</span></div>
                  <div>Recommend: <span className="font-mono text-primary">{s.scores.would_recommend_pct}%</span></div>
                </div>
                <div className="mt-2 flex gap-1 h-1.5 rounded-full overflow-hidden bg-muted">
                  <div className="h-full bg-secondary" style={{ width: `${s.sentiment.positive}%` }} title={`Positive ${s.sentiment.positive}%`} />
                  <div className="h-full bg-accent"     style={{ width: `${s.sentiment.neutral}%`  }} title={`Neutral ${s.sentiment.neutral}%`} />
                  <div className="h-full bg-destructive" style={{ width: `${s.sentiment.negative}%` }} title={`Negative ${s.sentiment.negative}%`} />
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  Sentiment: {s.sentiment.positive}% + {s.sentiment.neutral}% ~ {s.sentiment.negative}% − · dropout {s.dropout_rate_pct}% ({s.dropout_count}) · top reason: {s.top_dropout_reason || "—"}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-white p-6" data-testid="pv-dropout-reasons">
            <div className="overline mb-3">Portfolio dropout reasons</div>
            <ul className="space-y-3 text-sm">
              {data.dropout_reasons.map((d) => (
                <li key={d.reason}>
                  <div className="flex items-center justify-between">
                    <span className="text-primary font-medium">{d.reason}</span>
                    <span className="text-xs text-muted-foreground">{d.count}</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${(d.count / maxDrop) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-white p-6" data-testid="pv-themes">
            <div className="overline mb-3">Feedback themes (top study)</div>
            <ul className="space-y-2 text-sm">
              {data.per_study[0]?.themes.map((t) => (
                <li key={t.theme} className="flex items-center justify-between">
                  <span className="text-primary">{t.theme}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-${t.sentiment === "positive" ? "mint" : t.sentiment === "negative" ? "rose" : "sky"}-bg text-pastel-${t.sentiment === "positive" ? "mint" : t.sentiment === "negative" ? "rose" : "sky"}-fg`}>
                    {t.sentiment} · {t.mentions}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-pastel-peach-border bg-pastel-peach-bg p-6" data-testid="pv-signals">
        <div className="overline text-pastel-peach-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI patient-voice signals</div>
        <ul className="space-y-2 text-sm text-pastel-peach-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L15 · Investigators ----------------
const TIER_TONE = { Platinum: "lavender", Gold: "peach", Silver: "sky" };
function Investigators({ data }) {
  const t = data.totals;
  const maxPubs = Math.max(...data.investigators.map((p) => p.publications), 1);
  return (
    <div className="space-y-6" data-testid="cro-investigators">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Investigators" value={t.investigators} icon={UserCheck} tone="sky" testid="inv-total" />
        <KpiCard label="Platinum" value={t.platinum} unit={`/ ${t.investigators}`} icon={BadgeCheck} tone="lavender" testid="inv-platinum" />
        <KpiCard label="Avg score" value={t.avg_score} unit="/100" icon={TrendingUp} tone="mint" testid="inv-score" />
        <KpiCard label="GCP expiring 90d" value={t.gcp_expiring_90d} icon={AlertTriangle} tone="peach" testid="inv-expiring" />
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="inv-list">
        <div className="overline mb-3">Investigator directory · sorted by score</div>
        <div className="space-y-3">
          {data.investigators.map((pi) => (
            <div key={pi.name} className="border-t border-border pt-3 first:border-0 first:pt-0" data-testid={`inv-${pi.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-primary font-medium">{pi.name}</div>
                  <div className="text-[11px] text-muted-foreground">{pi.institution} · {pi.city} · {pi.specialty} · {pi.years} yrs</div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-${TIER_TONE[pi.tier]}-bg text-pastel-${TIER_TONE[pi.tier]}-fg border border-pastel-${TIER_TONE[pi.tier]}-border`}>
                    {pi.tier}
                  </span>
                  <span className="font-mono text-primary">{pi.score}/100</span>
                </div>
              </div>
              <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-muted-foreground">
                <div>Studies: <span className="text-primary font-medium">{pi.study_count}</span></div>
                <div>Velocity: <span className="text-primary font-medium">{pi.enrolment_velocity_per_month}/mo</span></div>
                <div>Publications: <span className="text-primary font-medium">{pi.publications}</span></div>
                <div>GCP: <span className={pi.gcp_status === "expiring" ? "text-destructive font-medium" : pi.gcp_status === "expired" ? "text-destructive font-medium" : "text-primary font-medium"}>{pi.gcp_expiry} ({pi.gcp_status})</span></div>
              </div>
              <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${(pi.publications / maxPubs) * 100}%` }} title={`${pi.publications} publications`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="inv-by-specialty">
          <div className="overline mb-3">By specialty</div>
          <ul className="space-y-2 text-sm">
            {data.by_specialty.map((sp) => (
              <li key={sp.specialty} className="flex items-center justify-between">
                <span className="text-primary font-medium">{sp.specialty}</span>
                <span className="text-xs text-muted-foreground">{sp.count} PI · avg score {sp.avg_score} · {sp.total_pubs} pubs</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-pastel-lavender-border bg-pastel-lavender-bg p-6" data-testid="inv-signals">
          <div className="overline text-pastel-lavender-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI investigator signals</div>
          <ul className="space-y-2 text-sm text-pastel-lavender-fg">
            {data.ai_signals.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
                <span>{s.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------------- L16 · RWE / Biomarkers ----------------
function RweBiomarkers({ data }) {
  const p = data.portfolio;
  return (
    <div className="space-y-6" data-testid="cro-rwe">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Studies analysed" value={p.studies} icon={FlaskConical} tone="sky" testid="rwe-count" />
        <KpiCard label="Avg biomarker+" value={`${p.avg_biomarker_positive_pct}%`} icon={Dna} tone="lavender" testid="rwe-biomarker" />
        <KpiCard label="Avg adherence" value={`${p.avg_adherence_pct}%`} icon={BadgeCheck} tone="mint" testid="rwe-adherence" />
        <KpiCard label="RWE matched cohort" value={p.total_rwe_cohort.toLocaleString("en-IN")} icon={Database} tone="peach" testid="rwe-cohort" />
      </div>
      <div className="space-y-4" data-testid="rwe-per-study">
        {data.per_study.map((s) => (
          <div key={s.id} className="rounded-lg border border-border bg-white p-6" data-testid={`rwe-study-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-primary font-medium">{s.name}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{s.therapeutic_area} · {s.phase}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-${s.rwe_signal === "concordant" ? "mint" : "peach"}-bg text-pastel-${s.rwe_signal === "concordant" ? "mint" : "peach"}-fg`}>
                RWE {s.rwe_signal}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="rounded-md bg-pastel-lavender-bg p-3">
                <div className="overline text-pastel-lavender-fg/80 mb-1">Biomarker</div>
                <div className="text-pastel-lavender-fg font-medium">{s.biomarker}</div>
                <div className="text-[11px] text-pastel-lavender-fg/80 mt-1">Positivity: <span className="font-mono">{s.biomarker_positive_pct}% ({s.biomarker_positive_n})</span></div>
                <div className="text-[11px] text-pastel-lavender-fg/80">Enrichment lift: <span className="font-mono">+{s.enrichment_lift_pp}pp</span></div>
              </div>
              <div className="rounded-md bg-pastel-mint-bg p-3">
                <div className="overline text-pastel-mint-fg/80 mb-1">Adherence & cohort</div>
                <div className="text-pastel-mint-fg font-mono text-lg">{s.adherence_pct}%</div>
                <div className="text-[11px] text-pastel-mint-fg/80 mt-1">RWE cohort: {s.rwe_matched_cohort.toLocaleString("en-IN")}</div>
              </div>
              <div className="rounded-md bg-muted/40 p-3">
                <div className="overline text-muted-foreground mb-1">Top comorbidities</div>
                <ul className="text-[11px] space-y-0.5">
                  {s.comorbidities.slice(0, 4).map((c) => (
                    <li key={c.name} className="flex justify-between"><span className="text-primary">{c.name}</span><span className="font-mono text-muted-foreground">{c.pct}%</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-pastel-mint-border bg-pastel-mint-bg p-6" data-testid="rwe-signals">
        <div className="overline text-pastel-mint-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI RWE / biomarker signals</div>
        <ul className="space-y-2 text-sm text-pastel-mint-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L17 · Predictive AI ----------------
const VERDICT_TONE = { green: "mint", amber: "peach", red: "rose" };
function Predictions({ data }) {
  const p = data.portfolio;
  return (
    <div className="space-y-6" data-testid="cro-predictions">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Avg success probability" value={`${p.avg_success_probability}%`} icon={Brain} tone="mint" testid="pred-success" />
        <KpiCard label="Avg enrolment forecast" value={`${p.avg_enrolment_forecast}%`} icon={TrendingUp} tone="sky" testid="pred-enrolment" />
        <KpiCard label="Avg dropout risk" value={`${p.avg_dropout_risk}%`} icon={AlertTriangle} tone="peach" testid="pred-dropout" />
        <KpiCard label="Portfolio verdict" value={`${p.green_studies}·${p.amber_studies}·${p.red_studies}`} unit="G·A·R" icon={ShieldAlert} tone="lavender" testid="pred-verdict" />
      </div>
      <div className="space-y-4" data-testid="pred-per-study">
        {data.per_study.map((s) => (
          <div key={s.id} className={`rounded-lg border border-pastel-${VERDICT_TONE[s.verdict]}-border bg-white p-6`} data-testid={`pred-study-${s.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-primary font-medium">{s.name}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{s.therapeutic_area} · {s.phase}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-${VERDICT_TONE[s.verdict]}-bg text-pastel-${VERDICT_TONE[s.verdict]}-fg border border-pastel-${VERDICT_TONE[s.verdict]}-border`}>
                  {s.verdict}
                </span>
                <span className="font-mono text-primary text-lg">{s.success_probability_pct}%</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div><span className="text-muted-foreground">Enrol forecast:</span> <span className="font-mono text-primary">{s.enrolment_forecast_pct}%</span></div>
              <div><span className="text-muted-foreground">Dropout risk:</span> <span className="font-mono text-primary">{s.dropout_risk_pct}%</span></div>
              <div><span className="text-muted-foreground">SAE spike risk:</span> <span className="font-mono text-primary">{s.sae_spike_risk_pct}%</span></div>
              <div><span className="text-muted-foreground">DB lock:</span> <span className="font-mono text-primary">{s.db_lock_date} ({s.db_lock_confidence_pct}%)</span></div>
            </div>
            <div className="mt-3 rounded-md bg-muted/40 p-2 text-[11px]">
              <div className="overline text-muted-foreground mb-1">Top drivers of risk</div>
              <ul className="space-y-0.5">
                {s.top_drivers.map((d) => (
                  <li key={d.factor} className="flex justify-between">
                    <span className="text-primary">{d.factor}</span>
                    <span className="font-mono text-muted-foreground">impact {d.impact}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-pastel-peach-border bg-pastel-peach-bg p-6" data-testid="pred-signals">
        <div className="overline text-pastel-peach-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI predictive signals</div>
        <ul className="space-y-2 text-sm text-pastel-peach-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L18 · Knowledge Graph ----------------
const NODE_TYPE_TONE = { study: "sky", sponsor: "mint", cro: "peach", site: "lavender", therapeutic_area: "lemon" };
function KnowledgeGraph({ data }) {
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="cro-graph">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Nodes" value={t.nodes} icon={Network} tone="sky" testid="graph-nodes" />
        <KpiCard label="Edges" value={t.edges} icon={Waypoints} tone="mint" testid="graph-edges" />
        <KpiCard label="Entity types" value={t.by_type.length} icon={Database} tone="lavender" testid="graph-types" />
        <KpiCard label="Studies covered" value={t.by_type.find((x) => x.type === "study")?.count || 0} icon={FlaskConical} tone="peach" testid="graph-studies" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6" data-testid="graph-by-type">
          <div className="overline mb-3">Entities by type</div>
          <ul className="space-y-2 text-sm">
            {t.by_type.map((b) => (
              <li key={b.type} className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full bg-pastel-${NODE_TYPE_TONE[b.type] || "sky"}-fg`} />
                  <span className="text-primary font-medium capitalize">{b.type.replace(/_/g, " ")}</span>
                </span>
                <span className="font-mono text-muted-foreground">{b.count}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-white p-6" data-testid="graph-top-nodes">
          <div className="overline mb-3">Top 10 most-connected entities</div>
          <ul className="space-y-2 text-sm">
            {data.top_nodes.map((n) => (
              <li key={n.id} className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-full bg-pastel-${NODE_TYPE_TONE[n.node.type] || "sky"}-fg`} />
                  <span className="text-primary font-medium truncate">{n.node.label}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{n.node.type}</span>
                </span>
                <span className="font-mono text-muted-foreground shrink-0">degree {n.degree}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="graph-edges-sample">
        <div className="overline mb-3">Edge sample · relationships</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          {data.edges.slice(0, 24).map((e, i) => {
            const src = data.nodes.find((n) => n.id === e.source);
            const dst = data.nodes.find((n) => n.id === e.target);
            return (
              <div key={i} className="flex items-center gap-1 truncate">
                <span className="text-primary truncate">{src?.label}</span>
                <span className="text-muted-foreground">— {e.type} →</span>
                <span className="text-primary truncate">{dst?.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-lg border border-pastel-sky-border bg-pastel-sky-bg p-6" data-testid="graph-signals">
        <div className="overline text-pastel-sky-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI graph signals</div>
        <ul className="space-y-2 text-sm text-pastel-sky-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- L19 · Enterprise Analytics ----------------
const KPI_STATUS_TONE = { above: "mint", at: "sky", below: "peach" };
function Enterprise({ data }) {
  const t = data.totals;
  const maxScore = Math.max(...data.departments.map((d) => d.score), 1);
  return (
    <div className="space-y-6" data-testid="cro-enterprise">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Portfolio value" value={`₹${t.budget_cr} Cr`} icon={IndianRupee} tone="sky" testid="ent-value" />
        <KpiCard label="Revenue" value={`₹${t.revenue_cr} Cr`} icon={TrendingUp} tone="mint" testid="ent-revenue" />
        <KpiCard label="ROI" value={`${t.roi_pct}%`} icon={BarChart3} tone="lavender" testid="ent-roi" />
        <KpiCard label="On-time delivery" value={`${t.on_time_delivery_pct}%`} icon={BadgeCheck} tone="peach" testid="ent-ontime" />
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="ent-kpis">
        <div className="overline mb-3">Portfolio KPIs vs benchmark</div>
        <ul className="space-y-3 text-sm">
          {data.kpis.map((k) => (
            <li key={k.name}>
              <div className="flex items-center justify-between">
                <span className="text-primary font-medium">{k.name}</span>
                <span className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-primary">{k.value}{k.unit}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-${KPI_STATUS_TONE[k.status]}-bg text-pastel-${KPI_STATUS_TONE[k.status]}-fg`}>
                    {k.status} · {k.vs_benchmark_pct > 0 ? "+" : ""}{k.vs_benchmark_pct}%
                  </span>
                </span>
              </div>
              <div className="mt-1 text-[10px] text-muted-foreground">Benchmark: {k.benchmark}{k.unit}</div>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg border border-border bg-white p-6" data-testid="ent-departments">
        <div className="overline mb-3">Department scorecards</div>
        <ul className="space-y-3 text-sm">
          {data.departments.map((d) => (
            <li key={d.name}>
              <div className="flex items-center justify-between">
                <span className="text-primary font-medium">{d.name}</span>
                <span className="text-xs text-muted-foreground">{d.headcount} FTE · util {d.utilisation_pct}% · on-track {d.on_track_pct}% · score <span className="font-mono text-primary">{d.score}</span></span>
              </div>
              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={`h-full ${d.score >= 85 ? "bg-secondary" : d.score >= 75 ? "bg-accent" : "bg-destructive"}`} style={{ width: `${(d.score / maxScore) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-lg border border-pastel-mint-border bg-pastel-mint-bg p-6" data-testid="ent-signals">
        <div className="overline text-pastel-mint-fg/80 mb-3 inline-flex items-center gap-2"><Sparkles size={12} /> AI enterprise signals</div>
        <ul className="space-y-2 text-sm text-pastel-mint-fg">
          {data.ai_signals.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${s.level === "high" ? "bg-destructive" : s.level === "medium" ? "bg-accent" : "bg-secondary"}`} />
              <span>{s.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


function CoPilot({ studyId }) {
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (!question) return;
    setBusy(true);
    setHistory((h) => [...h, { role: "user", text: question }]);
    setQ("");
    try {
      const { data } = await api.post("/cro/copilot/ask", { question, study_id: studyId });
      setHistory((h) => [...h, { role: "ai", text: data.answer, meta: `${data.studies_in_context} studies in context` }]);
    } catch (err) {
      toast.error(formatApiError(err));
      setHistory((h) => [...h, { role: "ai", text: "I couldn't answer that just now — please try again.", meta: "error" }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="cro-copilot">
      <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6 flex flex-col" style={{ minHeight: 480 }}>
        <div className="overline inline-flex items-center gap-2"><Sparkles size={12} /> Executive AI Co-Pilot</div>
        <div className="mt-4 flex-1 space-y-3 overflow-y-auto" data-testid="cro-copilot-history">
          {history.length === 0 && (
            <div className="text-sm text-muted-foreground">Ask anything about the portfolio — the co-pilot answers using the live study data.</div>
          )}
          {history.map((m, i) => (
            <div key={i} className={`rounded-md p-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary/5 text-primary" : "bg-muted/60 text-foreground"}`} data-testid={`cro-copilot-msg-${m.role}`}>
              {m.text}
              {m.meta && <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{m.meta}</div>}
            </div>
          ))}
          {busy && <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Thinking…</div>}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-4 flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask the co-pilot…" data-testid="cro-copilot-input" />
          <Button type="submit" disabled={busy || !q.trim()} data-testid="cro-copilot-send"><Send size={14} /></Button>
        </form>
      </div>
      <div className="rounded-lg border border-pastel-lavender-border bg-pastel-lavender-bg p-5">
        <div className="overline text-pastel-lavender-fg/80">Try asking</div>
        <ul className="mt-3 space-y-2">
          {SUGGESTED.map((s) => (
            <li key={s}>
              <button onClick={() => ask(s)} disabled={busy} className="w-full text-left text-sm text-pastel-lavender-fg hover:underline" data-testid={`cro-copilot-suggest-${s.slice(0,15)}`}>
                → {s}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
